import { create } from 'zustand';
import { PERCENT } from '../lib/device/catalog';

/**
 * Live state of a load as the guest has set it.
 *
 * This is the guest's own intent, not the device's word: it remembers the last
 * value the UI sent so a control stays where it was put. When the link hears the
 * RCU report the same channel *after* `at`, that reading takes over — see
 * `resolveLoadState`.
 */
export interface LoadRuntimeState {
  readonly power: boolean;
  readonly level: number;
  /** Epoch milliseconds of the guest's last touch; 0 when they never touched it. */
  readonly at: number;
  /**
   * Kept for hardware that reports an absolute position. Nothing in use does:
   * DIN curtain modules take a direction, so the guest control sends open, stop
   * or close and this value stays at its default.
   */
  readonly position: number;
}

const INITIAL_STATE: LoadRuntimeState = {
  power: false,
  level: PERCENT.DEFAULT,
  position: PERCENT.DEFAULT,
  at: 0,
};

interface LoadStateStore {
  readonly byLoadId: Readonly<Record<string, LoadRuntimeState>>;
  stateOf: (loadId: string) => LoadRuntimeState;
  setPower: (loadId: string, power: boolean) => void;
  setLevel: (loadId: string, level: number) => void;
  setPosition: (loadId: string, position: number) => void;
  /** Drops state for a load that no longer exists in the configuration. */
  forget: (loadId: string) => void;
  /**
   * Forgets every load's state at once.
   *
   * Used when the installation itself changes — an imported file, or a cleared
   * tablet. The intent is keyed by load id, and an id may be reused by a
   * different room's load, so keeping any of it would put a control where the
   * previous installation left it and call that the guest's own touch.
   */
  clear: () => void;
}

export const useLoadStateStore = create<LoadStateStore>((set, get) => {
  /** Every setter is a guest touch, so each one stamps the same clock. */
  const patch = (loadId: string, next: Partial<LoadRuntimeState>): void =>
    set(({ byLoadId }) => ({
      byLoadId: {
        ...byLoadId,
        [loadId]: { ...(byLoadId[loadId] ?? INITIAL_STATE), ...next, at: Date.now() },
      },
    }));

  return {
    byLoadId: {},

    stateOf: (loadId) => get().byLoadId[loadId] ?? INITIAL_STATE,

    setPower: (loadId, power) => patch(loadId, { power }),
    setLevel: (loadId, level) => patch(loadId, { level }),
    setPosition: (loadId, position) => patch(loadId, { position }),

    forget: (loadId) =>
      set(({ byLoadId }) => {
        const { [loadId]: _removed, ...rest } = byLoadId;
        return { byLoadId: rest };
      }),

    clear: () => set({ byLoadId: {} }),
  };
});

export default useLoadStateStore;
